<template>
  <div class="tw-flex tw-flex-col tw-h-full" data-testid="report-graphs-drawer">
    <div
      class="
        tw-flex
        tw-items-center
        tw-gap-1
        tw-px-4
        tw-shrink-0
        tw-border-solid
        tw-border-x-0
        tw-border-t-0
        tw-border-b
        tw-border-sf-neutral-lighter-2
      "
      :class="
        usingNewLayout ? 'tw-h-12' : 'tw-h-[var(--secondary-subheader-height)]'
      "
    >
      <SfButtonDs
        v-if="!store.fullscreen"
        v-tooltip="I18n.t('report.drawer.collapse')"
        icon-only
        icon-before="arrow-left"
        variant="secondary"
        :size="usingNewLayout ? 'sm' : 'lg'"
        data-testid="report-graphs-drawer-collapse"
        :aria-label="I18n.t('report.drawer.collapse')"
        @click="collapse"
      />
      <div class="tw-ml-auto tw-flex tw-items-center tw-gap-3">
        <label
          v-if="!readOnly"
          v-tooltip="pinToTopTooltip"
          class="
            tw-inline-flex
            tw-items-center
            tw-gap-2
            tw-text-sf-neutral-dark-1
            tw-m-0
          "
          :class="[
            { 'tw-cursor-pointer': !pinToTopDisabled },
            usingNewLayout ? 'tw-text-xs' : 'tw-text-sm',
          ]"
          data-testid="report-graphs-drawer-pin-to-top"
        >
          {{ pinToTopLabel }}
          <SfToggle
            :model-value="store.pinToTop"
            :icons="false"
            :disabled="pinToTopDisabled"
            :aria-label="pinToTopLabel"
            @update:model-value="onPinToTopChange"
          />
        </label>
        <SfButtonDs
          v-tooltip="fullscreenTooltip"
          icon-only
          :icon-before="store.fullscreen ? 'compress' : 'expand'"
          variant="secondary"
          :size="usingNewLayout ? 'sm' : 'lg'"
          data-testid="report-graphs-drawer-fullscreen-toggle"
          :aria-label="fullscreenTooltip"
          @click="
            store.fullscreen ? store.closeFullscreen() : store.openFullscreen()
          "
        />
      </div>
    </div>
    <div
      class="
        tw-flex-1
        tw-min-h-0
        tw-overflow-y-auto
        tw-p-6
        tw-flex
        tw-flex-col
        tw-gap-4
      "
    >
      <div
        v-if="showMainGroup"
        class="
          tw-bg-sf-slate-subtle-1
          tw-rounded-lg
          tw-p-4
          tw-flex
          tw-flex-col
          tw-gap-4
        "
      >
        <PinnedGraphCard
          :chart-height="pinnedChartHeight"
          :read-only="readOnly"
        />
        <HighlightedMetricsSection
          :fullscreen="store.fullscreen"
          :read-only="readOnly"
        />
      </div>
      <InsightMetricsSection
        :fullscreen="store.fullscreen"
        :read-only="readOnly"
      />
    </div>
    <ReportPaneResizer />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, toRef } from 'vue'

import PinnedGraphCard from '@app/application/report/components/graphs-drawer/pinned-graph-card.vue'
import HighlightedMetricsSection from '@app/application/report/components/graphs-drawer/highlighted-metrics-section.vue'
import InsightMetricsSection from '@app/application/report/components/graphs-drawer/insight-metrics-section.vue'
import ReportPaneResizer from '@app/application/report/components/report-pane-resizer.vue'
import { useReportGraphsDrawer } from '@app/application/report/store/use-report-graphs-drawer'
import { useReportPaneToggleHotkey } from '@app/application/report/composables/use-report-pane-toggle-hotkey'
import {
  PINNED_CHART_HEIGHT_DEFAULT,
  PINNED_CHART_HEIGHT_FULLSCREEN,
} from '@app/application/report/constants'
import SfButtonDs from '@app/shared/vue/components/sf-button/sf-button-ds.vue'
import SfToggle from '@app/shared/vue/components/sf-toggle.vue'

import useBodyClass from '@app/shared/vue/composables/use-body-class'
import useI18n from '@app/shared/vue/composables/use-I18n'
import { useDrawer } from '@app/shared/pinia/drawer'
import { LeftDrawerContent } from '@app/shared/types/drawer'
import {
  getOpenSidepanePreference,
  isUsingNewLayout,
} from '@app/adapters/silverfin'

// `report-graphs-drawer-fullscreen` on <body> drives page-level CSS that
// expands the graphs drawer (`aside#secondary-left-drawer` in the new layout
// or `aside#left-drawer` in legacy) and hides every other layout region. See
// `report-graphs-drawer-fullscreen.scss`.
const FULLSCREEN_BODY_CLASS = 'report-graphs-drawer-fullscreen'

const I18n = useI18n()
const drawerStore = useDrawer()
const store = useReportGraphsDrawer()
const usingNewLayout = isUsingNewLayout()

useReportPaneToggleHotkey()

// In presentation mode the drawer is consumption-only: every section gates
// its mutating affordances on `readOnly`.
const readOnly = window.Silverfin.presentationMode ?? false

const pinnedChartHeight = computed(() =>
  store.fullscreen
    ? PINNED_CHART_HEIGHT_FULLSCREEN
    : PINNED_CHART_HEIGHT_DEFAULT
)

const fullscreenTooltip = computed(() =>
  store.fullscreen
    ? I18n.t('report.drawer.fullscreen.close')
    : I18n.t('report.drawer.fullscreen.open')
)

// pinLocked: the pin is forced on, toggle is display-only.
// !pinnable: this report's pin can't be changed here.
const pinToTopDisabled = computed(() => store.pinLocked || !store.pinnable)

const pinToTopLabel = computed(() =>
  store.pinToTop
    ? I18n.t('report.drawer.pin_to_top.toggle_on')
    : I18n.t('report.drawer.pin_to_top.toggle_off')
)

const pinToTopTooltip = computed(() =>
  store.pinnable
    ? I18n.t('report.drawer.pin_to_top.tooltip')
    : I18n.t('report.drawer.locked_tooltip')
)

const showMainGroup = computed(
  () =>
    store.pinned !== null ||
    store.highlightedCount > 0 ||
    (!readOnly && !store.locked)
)

useBodyClass(FULLSCREEN_BODY_CLASS, toRef(store, 'fullscreen'))

function collapse(): void {
  if (usingNewLayout) {
    store.closeDrawer()
  } else {
    drawerStore.closeLeftDrawer()
  }
}

function onPinToTopChange(value: boolean): void {
  void store.setPinToTop(value)
}

onMounted(() => {
  // Presentation mode auto-opens the drawer regardless of any saved preference
  // — the drawer is the primary surface here.
  if (readOnly) {
    if (usingNewLayout) {
      store.openDrawer()
    } else {
      drawerStore.openDrawerDefault({
        direction: 'left',
        content: LeftDrawerContent.ReportGraphs,
      })
    }
    return
  }

  if (usingNewLayout) {
    // New layout hydrates `store.isOpen` from `currentUserPreferences` in the
    // mount behavior, so nothing to do here.
    return
  }

  const preference = getOpenSidepanePreference()

  if (
    preference?.position === 'left' &&
    preference?.content === LeftDrawerContent.ReportGraphs
  ) {
    drawerStore.openDrawerDefault({
      direction: 'left',
      content: LeftDrawerContent.ReportGraphs,
    })
  }
})
</script>
